import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import { io } from 'socket.io-client';
import { getMe } from 'User/Domain/Services/GetMeService';
import {login as loginApi} from 'Shared/Security/Domain/Services/LoginService';
import { token as loginToken } from 'Shared/Security/Domain/Services/TokenService';
import { impersonateUser as impersonateUserApi } from 'User/Domain/Services/ImpersonateUserService';
import { clearAllAvatarCache } from 'Shared/Utils/Domain/Services/AvatarUrlService';
import { PRIVATE } from 'Shared/Routing/Application/Constants/PathsConstant';

export const AuthContext = createContext();

const adminRoles = ['ROLE_ADMIN'];
export default function AuthContextProvider({children}) {

  const [meRefreshed, setMeRefreshed] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
  });
  const [isImpersonating, setIsImpersonating] = useState(() => {
    return !!localStorage.getItem('IMPERSONATOR_ORIGINAL_TOKEN');
  });
  const [socket] = useState(() => {
    return io(process.env.REACT_APP_WEBSOCKET_URL);
  });

  const [userState, setUserState] = useState(() => {
    let me = localStorage.getItem(process.env.REACT_APP_ME_NAME);
    if (!me) return null;
    try {
      return JSON.parse(me);
    } catch {
      return null;
    }
  });

  function resetAll() {
    localStorage.removeItem(process.env.REACT_APP_TOKEN_NAME);
    localStorage.removeItem(process.env.REACT_APP_REFRESH_TOKEN_NAME);
    localStorage.removeItem(process.env.REACT_APP_ME_NAME);
    localStorage.removeItem('IMPERSONATOR_ORIGINAL_TOKEN');
    localStorage.removeItem('IMPERSONATOR_ORIGINAL_REFRESH_TOKEN');
    localStorage.removeItem('IMPERSONATOR_ORIGINAL_ME');
    setIsImpersonating(false);
    setIsAuthenticated(false);
    setUserState(null);
    setMeRefreshed(false);
    clearAllAvatarCache();
  }

  const getMeFromLocalStorage = useCallback(function () {
    let me = localStorage.getItem(process.env.REACT_APP_ME_NAME);
    try {
      me = JSON.parse(me);
    } catch (e) {
      me = null;
    }
    return me;
  }, []);

  const getOriginalAdminFromLocalStorage = useCallback(function () {
    const raw = localStorage.getItem('IMPERSONATOR_ORIGINAL_ME');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }, []);

  const refreshMe = useCallback(async function () {
    const me = await getMe();
    if (me === null) {
      resetAll();
      return null;
    }
    localStorage.setItem(process.env.REACT_APP_ME_NAME, JSON.stringify(me));
    setUserState(me);
    return me;
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenInUrl = urlParams.get('token');
    if (!tokenInUrl && !meRefreshed && localStorage.getItem(process.env.REACT_APP_ME_NAME)) {
      setMeRefreshed(true);
      refreshMe();
    }
  }, [meRefreshed, refreshMe]);

  useEffect(() => {
    if (localStorage.getItem(process.env.REACT_APP_ME_NAME) && !socketConnected && isAuthenticated) {
      let me = getMeFromLocalStorage();
      if (me?.id) {
        socket.emit('frontend', {login: me.id});
        setSocketConnected(true);
      }
    }
  }, [socketConnected, isAuthenticated, getMeFromLocalStorage, socket]);

  const login = useCallback(async function (username, password) {
    try {
      const data = await loginApi(username, password);
      localStorage.setItem(process.env.REACT_APP_TOKEN_NAME, data.token);
      localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN_NAME, data.refresh_token);
      localStorage.removeItem('IMPERSONATOR_ORIGINAL_TOKEN');
      localStorage.removeItem('IMPERSONATOR_ORIGINAL_REFRESH_TOKEN');
      localStorage.removeItem('IMPERSONATOR_ORIGINAL_ME');
      setIsImpersonating(false);
      clearAllAvatarCache();
      const me = await getMe();
      if (me) {
        localStorage.setItem(process.env.REACT_APP_ME_NAME, JSON.stringify(me));
        setUserState(me);
        setMeRefreshed(true);
        if (socket && me.id) {
          socket.emit('frontend', {login: me.id});
        }
        setIsAuthenticated(true);
        return true;
      }
      resetAll();
      return false;
    } catch (error) {
      resetAll();
      return false;
    }
  }, [socket]);

  const loginWithToken = useCallback(async function (token, targetPath) {
    try {
      const data = await loginToken(token);
      localStorage.setItem(process.env.REACT_APP_TOKEN_NAME, data.token);
      localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN_NAME, data.refresh_token);
      localStorage.removeItem('IMPERSONATOR_ORIGINAL_TOKEN');
      localStorage.removeItem('IMPERSONATOR_ORIGINAL_REFRESH_TOKEN');
      localStorage.removeItem('IMPERSONATOR_ORIGINAL_ME');
      setIsImpersonating(false);
      clearAllAvatarCache();
      const me = await getMe();
      if (me) {
        localStorage.setItem(process.env.REACT_APP_ME_NAME, JSON.stringify(me));
        setUserState(me);
        setMeRefreshed(true);
        if (socket && me.id) {
          socket.emit('frontend', {login: me.id});
        }
        setIsAuthenticated(true);
        const destination = targetPath && typeof targetPath === 'string' && targetPath.trim() !== '' && targetPath !== 'null' && targetPath !== 'undefined'
          ? targetPath
          : PRIVATE;
        window.location.href = destination;
        return true;
      }
      resetAll();
      return false;
    } catch (error) {
      resetAll();
      return false;
    }
  }, [socket]);

  const impersonate = useCallback(async function (userId) {
    const data = await impersonateUserApi(userId);
    if (!data || !data.token) {
      throw new Error("No se pudo obtener el token para suplantar usuario");
    }

    if (!localStorage.getItem('IMPERSONATOR_ORIGINAL_TOKEN')) {
      const origToken = localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
      const origRefreshToken = localStorage.getItem(process.env.REACT_APP_REFRESH_TOKEN_NAME);
      const origMe = localStorage.getItem(process.env.REACT_APP_ME_NAME);

      if (origToken) {
        localStorage.setItem('IMPERSONATOR_ORIGINAL_TOKEN', origToken);
      }
      if (origRefreshToken) {
        localStorage.setItem('IMPERSONATOR_ORIGINAL_REFRESH_TOKEN', origRefreshToken);
      }
      if (origMe) {
        localStorage.setItem('IMPERSONATOR_ORIGINAL_ME', origMe);
      }
    }

    localStorage.setItem(process.env.REACT_APP_TOKEN_NAME, data.token);
    if (data.refresh_token) {
      localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN_NAME, data.refresh_token);
    }
    const targetMe = await getMe();
    if (targetMe) {
      localStorage.setItem(process.env.REACT_APP_ME_NAME, JSON.stringify(targetMe));
      setUserState(targetMe);
      clearAllAvatarCache();
      if (socket && targetMe.id) {
        socket.emit('frontend', {login: targetMe.id});
      }
    }
    setIsImpersonating(true);
    window.location.href = '/private';
  }, [socket]);

  const revertImpersonation = useCallback(function () {
    const origToken = localStorage.getItem('IMPERSONATOR_ORIGINAL_TOKEN');
    const origRefreshToken = localStorage.getItem('IMPERSONATOR_ORIGINAL_REFRESH_TOKEN');
    const origMe = localStorage.getItem('IMPERSONATOR_ORIGINAL_ME');

    if (origToken) {
      localStorage.setItem(process.env.REACT_APP_TOKEN_NAME, origToken);
      if (origRefreshToken) {
        localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN_NAME, origRefreshToken);
      } else {
        localStorage.removeItem(process.env.REACT_APP_REFRESH_TOKEN_NAME);
      }
      if (origMe) {
        localStorage.setItem(process.env.REACT_APP_ME_NAME, origMe);
        try {
          setUserState(JSON.parse(origMe));
        } catch {
          setUserState(null);
        }
      }
    }

    localStorage.removeItem('IMPERSONATOR_ORIGINAL_TOKEN');
    localStorage.removeItem('IMPERSONATOR_ORIGINAL_REFRESH_TOKEN');
    localStorage.removeItem('IMPERSONATOR_ORIGINAL_ME');
    setIsImpersonating(false);
    clearAllAvatarCache();

    window.location.href = '/private/admin/list-users';
  }, []);

  const logout = useCallback(function () {
    resetAll();
  }, []);

  const isAdmin = useCallback(function () {
    let me = getMeFromLocalStorage();
    if (!me) {
      return false;
    }
    let roles = me.roles;
    if (roles) {
      return adminRoles.some((role) => roles.includes(role));
    }
    return false;
  }, [getMeFromLocalStorage]);

  const checkPermission = useCallback(function (permission) {
    let me = getMeFromLocalStorage();
    if (!me) {
      return false;
    }
    let permissions = me.permissions;
    if (permissions) {
      return permissions.some((perm) => perm.sku === permission);
    }
    return false;
  }, [getMeFromLocalStorage]);

  const value = useMemo(
    () => ({
      login,
      loginWithToken,
      logout,
      isAuthenticated,
      isImpersonating,
      impersonate,
      revertImpersonation,
      getOriginalAdminFromLocalStorage,
      socket,
      isAdmin,
      checkPermission,
      getMeFromLocalStorage,
      refreshMe,
      userState
    }),
    [
      isAuthenticated,
      isImpersonating,
      impersonate,
      revertImpersonation,
      getOriginalAdminFromLocalStorage,
      login,
      logout,
      socket,
      isAdmin,
      checkPermission,
      loginWithToken,
      getMeFromLocalStorage,
      refreshMe,
      userState
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthContextProvider.propTypes = {
  children: PropTypes.object
};

export function useAuthContext() {
  return useContext(AuthContext);
}

